import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";

import FormDrawer from "../common/form-drawer";
import { ButtonLoader, Input, SelectInput } from "../common/form";
import * as walletAction from 'store/entities/wallet/action';
import * as notificationAction from "store/entities/notification/action";
import { CurrencyFormatWithoutSymbol } from "utils/currencyFormat";

const MoveMoneyBetweenAccounts = (props) => {
    const { walletId } = useParams();
    const dispatch = useDispatch();
    const { businessAccounts, businessAccountsLoading, moveMoneyLoading, moveMoney } = useSelector(s => s.entities.wallet);
    const {
        isOpen,
        setIsOpen,
        accountDetails,
    } = props;

    const [form, setForm] = useState({
        amountInKobo: "",
        recipientWalletId: "",
        senderWalletId: "",
        narration: ""
    })

    const submitForm = (e) => {
        e.preventDefault();
        const payload = {
            amountInKobo: Number(form?.amountInKobo?.split(",").join("")) * 100, // Multiply amount by 100 to give amount in kobo
            recipientWalletId: form?.recipientWalletId,
            senderWalletId: form?.senderWalletId.toString(),
            narration: form?.narration
        };

        dispatch(walletAction.moveMoney(payload));
    }

    const getAccountDetails = () => {
        dispatch(walletAction.getAccountDetails(String(walletId || accountDetails?.walletId)));
        dispatch(walletAction.getAccountTransactions(String(walletId || accountDetails?.walletId)));
    }
    
    const fetchBusinessAccounts = () => {
        dispatch(walletAction.getBusinessAccounts());
    }

      /**
     * CASE 1: If the operating account is collection, pick and ONLY send money to primary account
     * CASE 2: If the operating account is expense, pick and ONLY send money to expense accounts including primary account
     */
    const filterAccounts = () => {
        if (businessAccounts?.status === "success") {
            if (accountDetails?.walletType?.toLowerCase() === "collection") {
                return businessAccounts?.data?.filter(account => account?.isPrimaryAccount === true);
            } else if (accountDetails?.walletType?.toLowerCase() === "expense") {
                return businessAccounts?.data?.filter(account => account?.walletType.toLowerCase() === 'expense' && account?.walletId !== accountDetails?.walletId);
            }
        }
        return [];
    };

    const accounts = filterAccounts();

    const isFormValid = !form?.recipientWalletId || !form?.narration || !form?.amountInKobo || !form?.senderWalletId;

    useEffect(() => {
        if (accountDetails) {
            setForm({
                senderWalletId: accountDetails?.walletId,
            });
        }
    }, [accountDetails]);

    useEffect(() => {
        if (moveMoney?.status === "success") {
            setIsOpen(false);
            toast.success(moveMoney?.message);
            getAccountDetails()
            dispatch(walletAction.resetMoveMoney());
            dispatch(notificationAction.getAllNotifications());
            fetchBusinessAccounts();
        }
    }, [moveMoney, dispatch]);

    useEffect(() => {
        fetchBusinessAccounts();
    }, [])

    // Pre-fill and disable the dropdown if sending from a collection account to the primary account
    useEffect(() => {
        if (accountDetails?.walletType?.toLowerCase() === "collection") {
            const primaryAccount = businessAccounts?.data?.find(account => account?.isPrimaryAccount === true);
            if (primaryAccount) {
                setForm((prevForm) => ({
                    ...prevForm,
                    recipientWalletId: primaryAccount.walletId
                }));
            }
        }
    }, [accountDetails, businessAccounts]);

    const isDropdownDisabled = accountDetails?.walletType?.toLowerCase() === "collection";

    return (
        <>

            <FormDrawer display="center" size="lg" isOpen={isOpen} setIsOpen={setIsOpen}>
                <div className="p-6">
                    <FormDrawer.Title
                        title="Move Money"
                        description="Send money to your primary or sub-accounts"
                        showCloseButton={true}
                        close={setIsOpen}
                    />
                    <hr className="w-full h-px mt-2 bg-[#E2E4E8] border-none" />

                    <form onSubmit={submitForm} className="mt-8">
                        <div className="my-4">
                            <div className="px-3 py-1 mb-2 bg-[#F9FAFB] rounded-xl">
                                <Input
                                    type="text"
                                    label="From"
                                    disabled={true}
                                    required={false}
                                    showBalance={true}
                                    balanceFigure={accountDetails?.balance}
                                    value={accountDetails?.accountName}
                                    inputClass="mt-1"
                                />

                                <SelectInput
                                    label="To"
                                    disabled={isDropdownDisabled}
                                    value={form?.recipientWalletId}
                                    onChange={(e) => setForm({ ...form, recipientWalletId: e.target.value })}
                                >
                                    <option value="">{businessAccountsLoading ? "Fetching accounts..." : "Select Account"}</option>
                                    {accounts?.map(account => (
                                        <option key={account?._id} value={account?.walletId}>{account?.accountName}</option>
                                    ))}
                                </SelectInput>
                            </div>

                            <Input
                                type="text"
                                label="Amount (Naira)"
                                placeholder="0.00"
                                required={false}
                                value={form?.amountInKobo || ""}
                                inputClass="mt-1"
                                showInsufficientBalanceValidation={(Number(form?.amountInKobo?.split(",").join("")) * 100) > accountDetails?.balance}
                                onChange={(e) => {
                                    const { value } = e.target;
                                    if (value === "0") return;
                                    const cleanedValue = value.replace(/[^\d.]/g, '');
                                    const [integerPart, decimalPart] = cleanedValue.split('.');
                                    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                                    let formattedValue = formattedInteger;
                                    if (decimalPart !== undefined) {
                                        formattedValue += '.' + decimalPart.slice(0, 2);
                                    }
                                    setForm({
                                        ...form, 
                                        amountInKobo: formattedValue,
                                    })
                                }}
                            />

                            <Input
                                type="text"
                                label="Narration"
                                placeholder="Enter Narration"
                                required={false}
                                value={form?.narration || ""}
                                inputClass="mt-1"
                                showCharacterLength={true}
                                displayBesideLabel={true}
                                characterCount={form?.narration?.length}
                                maxLength={50}
                                onChange={(e) => setForm({...form, narration: e.target.value})}
                            />
                        </div>

                        <div className="mt-10 flex justify-end space-x-4">
                            <button type="button" onClick={() => setIsOpen(false)} className="btn btn-lg btn-transparent-primary">
                                Cancel
                            </button>
                            <ButtonLoader disabled={isFormValid} loading={moveMoneyLoading} type="submit" className="btn btn-lg btn-ep-primary">
                                Move Money
                            </ButtonLoader>
                        </div>
                    </form>
                </div>
            </FormDrawer>
        </>
    )
}

export default MoveMoneyBetweenAccounts;